import axios from 'axios';
import {
	API_ENDPOINTS,
	BottomAnchoredControls,
	Button,
	ButtonTypes,
	Card,
	CardBody,
	customToast,
	PageLayout,
	replaceKeyWithValue,
	route,
	StringHelpers,
	SubTitle,
	useDisabledContext,
	useForm,
	useLangContext,
	Variants,
	WidthConstrainedContainer,
	PostcodeLookup,
	useAuthorisationContext,
	Spinner,
	GasSafeLookup,
	checkTheme,
	Themes,
	PhoneNumberField,
	useUserContext,
	OptionsType,
	Roles,
} from 'carrier-fe';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { UserViewType } from '../../../types/user';

const defaultData = {
	first_name: '',
	last_name: '',
	email: '',
	username: '',
	mobile_phone_prefix: '',
	mobile_phone_number: '',
	landline_phone_prefix: '',
	landline_phone_number: '',
	address_id: '',
	local_authority_code: '',
	address_line_1: '',
	address_line_2: '',
	address_line_3: '',
	town_city: '',
	state_county: '',
	postcode_zipcode: '',
	country: '',
	sub_country_name: '',
	company_id: '',
	role_id: '',
	system_access_enabled: '1',
	gas_safe_licence_card_number: '',
	gas_safe_confirmed: false,
	username_update: '1',
	training_id: '',
};

type Data = typeof defaultData;

function UserForm() {
	const { userId } = useParams();
	const isEdit = userId ? true : false;

	const { user } = useUserContext();
	const { crud, fields } = useLangContext();
	const { disabled } = useDisabledContext();
	const navigate = useNavigate();

	const [isFetching, setIsFetching] = useState(false);
	const [loading, setLoading] = useState(false);
	const [roles, setRoles] = useState<OptionsType>([]);
	const [companies, setCompanies] = useState<OptionsType>([]);
	const { hasRoleAccess } = useAuthorisationContext();
	const [searchParams] = useSearchParams();
	const lastCompanyIdRef = useRef<string | undefined>(undefined);
	const appliedCompanyIdRef = useRef<string | undefined>(undefined);
	const [recordLoaded, setRecordLoaded] = useState(!isEdit);

	const canAssignAdminRoles =
		hasRoleAccess(Roles.SUPER_ADMIN) || hasRoleAccess(Roles.PLATFORM_MANAGER);

	const isPrivilegedUser =
		canAssignAdminRoles || hasRoleAccess(Roles.DISTRIBUTION_MANAGER);

	const fetchData = async () => {
		if (isFetching) return;
		setIsFetching(true);
		setLoading(true);

		try {
			const { data } = await axios.get<{ data: UserViewType }>(
				route(API_ENDPOINTS.ADMIN.USER.VIEW, { userId: String(userId) })
			);

			const flattenedData = {
				...data.data,
				...data.data.individual,
				...data.data.individual.address,
				company_id: data.data.individual.company?.id || '',
				username_update: '0',
			};

			setValues(flattenedData);
		} catch (error: any) {
			customToast({
				title: error?.response?.data?.message || 'An error occurred.',
				variant: Variants.Danger,
			});
		} finally {
			// Set on the failure path too, so a role list is still fetched against the
			// empty default rather than the form being left with no options at all.
			setRecordLoaded(true);
			setIsFetching(false);
			setLoading(false);
		}
	};

	/*
	 * Deliberately outside the shared isFetching guard the other two fetchers use.
	 * That guard is re-entrancy protection for unrelated requests, and it made this
	 * one a coin toss: the role list is refetched whenever the company changes, which
	 * on an edit lands while the company list is still in flight, and the fetch was
	 * then dropped with no retry. lastCompanyIdRef already discards stale responses,
	 * which is the only ordering guarantee this needs.
	 */
	const fetchRoles = async (companyId?: string) => {
		lastCompanyIdRef.current = companyId;

		try {
			const { data } = await axios.get<{ data: OptionsType }>(
				route(API_ENDPOINTS.GENERIC.SELECT.COMPANY_ROLES, {
					company: companyId ? companyId : '',
				})
			);
			if (lastCompanyIdRef.current === companyId) {
				setRoles(data.data);
			}
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'An error occurred. Please try again.',
				variant: Variants.Danger,
			});
		}
	};

	const fetchCompanies = async () => {
		if (isFetching) return;
		setIsFetching(true);

		try {
			const { data } = await axios.get<{
				data: OptionsType;
			}>(
				route(API_ENDPOINTS.GENERIC.SELECT.COMPANIES) +
					'?only_approved=1'
			);
			setCompanies(data.data);
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'An error occurred. Please try again.',
				variant: Variants.Danger,
			});
		} finally {
			setIsFetching(false);
		}
	};

	const update = async (payload: typeof defaultData) => {
		return isEdit
			? axios.patch<{ data: Data; message: string }>(
					route(API_ENDPOINTS.ADMIN.USER.UPDATE, {
						userId: String(userId),
					}),
					payload
			  )
			: axios.post<{ data: Data; message: string }>(
					route(API_ENDPOINTS.ADMIN.USER.STORE),
					payload
			  );
	};

	const { Input, Submit, setValues, data, errors, store } = useForm(
		update,
		defaultData
	);

	useEffect(() => {
		if (isEdit) {
			fetchData();
		}
		fetchCompanies();
	}, []);

	useEffect(() => {
		if (data) {
			navigate(`/user/${data.id}`);
		}
	}, [data]);

	useEffect(() => {
		if (store.username_update === '1') setValues({ username: store.email });
	}, [store.email]);

	useEffect(() => {
		const companyId = searchParams.get('companyId');

		if (companyId) {
			setValues({ company_id: companyId });
		}
	}, [searchParams]);

	useEffect(() => {
		if (!isPrivilegedUser) {
			setValues({ company_id: user?.individual?.company_id });
		}
	}, [isPrivilegedUser]);

	/*
	 * Single source of truth for the role list, which the API scopes to the selected
	 * company - or to the admin roles when there is no company, the only way to reach
	 * Platform Manager (CUS-1061). Clearing the company select fires onChange(null),
	 * so this has to react to an emptied value and not just a truthy one.
	 *
	 * On an edit the fetch waits for the record, otherwise the mount would ask for
	 * admin roles against the still-empty default before the user's own company
	 * arrives, and the reset below would drop a role that is perfectly valid.
	 * recordLoaded is state rather than a ref because it has to be a dependency in
	 * its own right - a user who already has no company leaves company_id on its
	 * empty default, so the load is the only thing that changes.
	 */
	useEffect(() => {
		if (!recordLoaded) return;

		const companyId = store.company_id || '';

		// null, undefined and '' all mean the same company here, but are distinct
		// effect dependencies - without this they fetch the same list twice.
		if (appliedCompanyIdRef.current === companyId) return;

		appliedCompanyIdRef.current = companyId;

		fetchRoles(companyId || undefined);
	}, [store.company_id, recordLoaded]);

	/*
	 * A role selected against the previous company is not in the new company's set.
	 * Left in place it is submitted unchanged and the API rejects it on Rule::in(),
	 * so drop it and make the user pick from what is now on offer.
	 */
	useEffect(() => {
		if (!store.role_id || roles.length === 0) return;

		if (!roles.some((role) => role.value === store.role_id)) {
			setValues({ role_id: '' });
		}
	}, [roles]);

	const title = StringHelpers.title(
		isEdit
			? replaceKeyWithValue(
					crud?.pages?.edit.title || 'Edit :model',
					'model',
					crud?.models?.user || 'User'
			  )
			: replaceKeyWithValue(
					crud?.pages?.create.title || 'Create :model',
					'model',
					crud?.models?.user || 'User'
			  )
	);

	const companyManagerIsChangingTheirOwnRole = useMemo(
		() => !isPrivilegedUser && isEdit && user?.id === userId, 
		[isPrivilegedUser, isEdit, user?.id, userId]
	);

	return (
		<PageLayout title={StringHelpers.title(title)}>
			<WidthConstrainedContainer>
				{loading ? (
					<div className="d-flex justify-content-center align-items-center vh-100">
						<Spinner />
					</div>
				) : (
					<>
						<Card>
							<CardBody>
								<SubTitle
									title={StringHelpers.title(
										crud?.sub_titles?.details || 'Details'
									)}
									className="mb-3"
									style={{ color: '#464C5E' }}
								/>
								<div className="row">
									<div className="col-12 col-md-6">
										{Input({
											name: 'first_name',
											label:
												fields?.first_name ||
												'First Name',
										})}
									</div>
									<div className="col-12 col-md-6">
										{Input({
											name: 'last_name',
											label:
												fields?.last_name ||
												'Last Name',
										})}
									</div>
								</div>
								{Input({
									name: 'email',
									label: fields?.email || 'Email',
								})}
								{!checkTheme(Themes.Toshiba) && 
									<>
										{Input({
											name: 'username',
											label: fields?.username || 'Username',
											disable: true,
										})}

										{isEdit && store.email !== store.username && (
											<div className="d-flex gap-2 w-100 align-items-center">
												<p className="w-100">
													{fields.update_username_to_match_email ||
														'Would you like to update the username to match the email?'}
												</p>
												<div className="w-25">
													{Input({
														name: 'username_update',
														label: 'Update username',
														type: 'select',
														options: crud.options.boolean,
													})}
												</div>
											</div>
										)}
									</>
								}
								{Input({
									name: 'company_id',
									label: fields?.company || 'Company',
									type: 'searchable-select',
									options: companies,
									disable: companies.length === 0,
									visible: isPrivilegedUser,
								})}
								{Input({
									name: 'role_id',
									label: fields?.role || 'Role',
									placeholder: StringHelpers.title(
										fields?.please_select || 'Please select'
									),
									type: 'select',
									options: roles,
									// Only the two roles that can actually assign an admin role are
									// told about it - for anyone else the company is fixed and the
									// hint would point at a field they cannot act on.
									description: canAssignAdminRoles
										? fields?.platform_roles_have_no_company ||
										  'Platform-level roles are not tied to a company - clear the Company field to choose one.'
										: undefined,
									visible: !companyManagerIsChangingTheirOwnRole
								})}
								<PhoneNumberField
									label={StringHelpers.title(
										fields.mobile_phone_number ||
											'Mobile number'
									)}
									name="mobile_phone_number"
									value={store?.mobile_phone_number || ''}
									onChange={(value) =>
										setValues({
											mobile_phone_number: value,
										})
									}
									disabled={disabled}
									errorMessages={errors?.mobile_phone_number || errors?.mobile_phone_prefix}
									prefixValue={store?.mobile_phone_prefix || ''}
									prefixOnChange={(value) =>
										setValues({
											mobile_phone_prefix: value,
										})
									}
								/>
								<PhoneNumberField
									label={StringHelpers.title(
										fields?.landline_phone_number ||
											'Landline Number'
									)}
									name="landline_phone_number"
									value={store?.landline_phone_number || ''}
									onChange={(value) =>
										setValues({
											landline_phone_number: value,
										})
									}
									disabled={disabled}
									errorMessages={errors?.landline_phone_number || errors?.landline_phone_prefix}
									prefixValue={store?.landline_phone_prefix || ''}
									prefixOnChange={(value) =>
										setValues({
											landline_phone_prefix: value,
										})
									}
								/>
								{checkTheme(Themes.Toshiba) &&
									Input({
										name: 'training_id',
										label: fields?.engineer_training_id
											|| 'Engineer Training ID',
										capitaliseLabel: false,
										disable: !hasRoleAccess(Roles.COMPANY_MANAGER)
											&& !hasRoleAccess(Roles.PLATFORM_MANAGER)
											&& !hasRoleAccess(Roles.SUPER_ADMIN)
									})
								}
								{Input({
									name: 'system_access_enabled',
									label:
										fields?.system_access_enabled ||
										'Enable System Access',
									type: 'select',
									options: crud?.options?.boolean,
								})}
							</CardBody>
						</Card>

						<Card>
							<CardBody>
								<PostcodeLookup
									data={store}
									setData={setValues}
									errors={errors}
								/>
							</CardBody>
						</Card>

						{checkTheme(Themes.Viessmann) && (
							<Card>
								<CardBody>
									<SubTitle
										title={
											crud?.sub_titles?.gas_safe ||
											'GasSafe' +
												' ' +
												crud?.sub_titles?.details ||
											'Details'
										}
										className="mb-3 w-100"
										style={{ color: '#464C5E' }}
									/>
									<GasSafeLookup
										fetchOnLoad={isEdit}
										displayCard
										onGasSafeNumberChange={(v) => {
											setValues({
												gas_safe_licence_card_number: v,
											});
										}}
										onGasSafeConfirmedChange={(v) => {
											setValues({
												gas_safe_confirmed: v,
											});
										}}
										disabled={disabled}
										gasSafeNumber={
											store?.gas_safe_licence_card_number
										}
										gasSafeConfirmed={
											store?.gas_safe_confirmed
										}
										labels={{
											gasSafeNumber:
												fields?.gas_safe_licence_card_number,
											gasSafeConfirmed:
												fields?.gas_safe_confirmation_generic,
										}}
										errors={{
											gasSafeNumber:
												errors?.gas_safe_licence_card_number ||
												'',
											gasSafeConfirmed:
												errors?.gas_safe_confirmed ||
												'',
										}}
										type={'engineer'}
									/>
								</CardBody>
							</Card>
						)}
					</>
				)}
			</WidthConstrainedContainer>
			<BottomAnchoredControls>
				<Button
					label={crud?.buttons.back.default || 'Back'}
					onClick={() =>
						isEdit ? navigate(`/user/${userId}`) : navigate('/user')
					}
					variant={Variants.Dark}
					type={ButtonTypes.Outline}
					className={'me-4'}
					disabled={disabled}
				/>
				<Submit
					defaultLabel={
						isEdit
							? crud?.buttons.edit.default || 'Update'
							: crud?.buttons.create.default || 'Create'
					}
					loadingLabel={
						isEdit
							? crud?.buttons.edit.submitting || 'Updating...'
							: crud?.buttons.create.submitting || 'Creating...'
					}
					variant={Variants.Info}
				/>
			</BottomAnchoredControls>
		</PageLayout>
	);
}

export default UserForm;
